import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Avatar,
  useMediaQuery,
  alpha,
  Fade,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import axiosInstance from '../axiosInstance';
import PasswordAccordion from './PasswordAccordion';
import ProfileImageAccordion from './ProfileImageAccordion';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EmailIcon from '@mui/icons-material/Email';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import PersonIcon from '@mui/icons-material/Person';
import { useTheme } from '@mui/material/styles';

const ProfilePage = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | false>(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDarkTheme = theme.name === 'dark';

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await axiosInstance.get('/auth/me');
        setUserData(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching user data:', error);
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleAccordionChange = (panel: string) => (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpanded(isExpanded ? panel : false);
  };

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          gap: 2
        }}
      >
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">Loading profile...</Typography>
      </Box>
    );
  }

  const iconColors = [theme.palette.primary.main, theme.palette.primary.light];

  return (
    <Box 
      sx={{ 
        backgroundColor: 'background.default', 
        minHeight: '100vh', 
        py: 4,
        transition: 'all 0.3s ease-in-out'
      }}
    >
      <Container 
        maxWidth="lg" 
        sx={{ 
          p: { xs: 1, md: 3 },
          margin: '0 auto',
          transition: 'all 0.3s ease-in-out'
        }}
      >
        <Fade in={true} timeout={500}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: { xs: 2, md: 4 },
              background: isDarkTheme ? alpha(theme.palette.background.paper, 0.6) : theme.palette.background.paper,
              borderRadius: '8px',
              backdropFilter: 'blur(10px)',
              borderLeft: '4px solid',
              borderColor: 'primary.main',
              mb: 3
            }}
          >
            {userData && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <PersonIcon sx={{ fontSize: 32, mr: 2, color: theme.palette.primary.main }} />
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    User Profile
                  </Typography>
                </Box>

                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', md: 'row' }, 
                  gap: { xs: 3, md: 4 }, 
                  alignItems: { xs: 'center', md: 'flex-start' }
                }}>
                  {/* Left side - Avatar with shadow effect */}
                  <Box sx={{ 
                    width: { xs: '100%', md: '35%' }, 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    flexDirection: 'column',
                    pb: { xs: 3, md: 0 },
                    borderBottom: { xs: `1px solid ${alpha(theme.palette.divider, 0.1)}`, md: 'none' },
                  }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: '50%', 
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.6)}, ${alpha(theme.palette.primary.dark, 0.2)})`,
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.2)}`,
                      mb: 2
                    }}>
                      <Avatar 
                        src={userData.profile_image_path} 
                        alt="Profile Image" 
                        sx={{ 
                          width: { xs: '120px', sm: '140px', md: '160px' }, 
                          height: { xs: '120px', sm: '140px', md: '160px' }, 
                          borderRadius: '50%',
                          border: '4px solid', 
                          borderColor: isDarkTheme ? alpha('#fff', 0.9) : '#fff',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                        }} 
                      />
                    </Box>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                      {userData.username}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ 
                      px: 3, 
                      py: 1, 
                      borderRadius: '20px',
                      bgcolor: isDarkTheme ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.primary.main, 0.05),
                      color: theme.palette.primary.main,
                      fontWeight: 'medium',
                      display: 'inline-flex',
                      alignItems: 'center'
                    }}>
                      <SecurityIcon sx={{ fontSize: '0.9rem', mr: 0.5 }} />
                      {userData.role}
                    </Typography>
                  </Box>

                  {/* Right side - User info */}
                  <Box sx={{ 
                    width: { xs: '100%', md: '65%' }, 
                    display: 'flex', 
                    flexDirection: 'column'
                  }}>
                    <Card 
                      elevation={0} 
                      sx={{ 
                        bgcolor: isDarkTheme ? alpha(theme.palette.background.secondary, 0.3) : alpha(theme.palette.background.secondary, 0.2),
                        borderRadius: isDarkTheme ? '12px' : '4px',
                        mb: 2,
                        overflow: 'hidden'
                      }}
                    >
                      <CardContent sx={{ px: 3, pt: 2, pb: 3 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <EmailIcon sx={{ color: iconColors[1], fontSize: 24 }} />
                            <Box>
                              <Typography variant="body2" color="text.secondary">Email</Typography>
                              <Typography variant="subtitle1">{userData.email}</Typography>
                            </Box>
                          </Box>
                          
                          <Divider sx={{ my: 0.5 }} />
                          
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <VerifiedUserIcon sx={{ color: iconColors[0], fontSize: 24 }} />
                            <Box>
                              <Typography variant="body2" color="text.secondary">Status</Typography>
                              <Typography variant="subtitle1">
                                {userData.is_active ? (
                                  <Box component="span" sx={{ 
                                    color: 'success.main',
                                    fontWeight: 'medium' 
                                  }}>
                                    Active
                                  </Box>
                                ) : (
                                  <Box component="span" sx={{ 
                                    color: 'error.main',
                                    fontWeight: 'medium' 
                                  }}>
                                    Inactive
                                  </Box>
                                )}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>

                    <Box sx={{ mt: 2 }}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2.5,
                          bgcolor: isDarkTheme 
                            ? alpha(theme.palette.info.main, 0.1) 
                            : alpha(theme.palette.info.main, 0.05),
                          borderRadius: isDarkTheme ? '12px' : '4px',
                          border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                          mb: 2
                        }}
                      >
                        <Typography variant="body1" sx={{ color: isDarkTheme ? alpha(theme.palette.info.main, 0.9) : theme.palette.info.main }}>
                          Use the settings below to update your password or profile image.
                        </Typography>
                      </Paper>
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </Paper>
        </Fade>
        
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          gap: 3,
          justifyContent: 'space-between'
        }}>
          <Box sx={{ width: { xs: '100%', md: '48%' } }}>
            <PasswordAccordion expanded={expanded} handleAccordionChange={handleAccordionChange} />
          </Box>
          <Box sx={{ width: { xs: '100%', md: '48%' } }}>
            <ProfileImageAccordion expanded={expanded} handleAccordionChange={handleAccordionChange} userData={userData} setUserData={setUserData} />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ProfilePage;
